import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Pagination, Alert, Badge, Modal, InputGroup } from 'react-bootstrap';
import { Search, Filter, Eye, Calendar } from 'lucide-react';
import { emailLogsService, emailConfigService } from '../services/emailService';

const EmailLogsTable = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(25);

  // Filters
  const [filters, setFilters] = useState({
    sender: '',
    receiver: '',
    status: '',
    startDate: '',
    endDate: '',
    configurationId: ''
  });

  const [configurations, setConfigurations] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [stats, setStats] = useState(null);

  // Load configurations for filter
  useEffect(() => {
    const loadConfigurations = async () => {
      try {
        const response = await emailConfigService.getAllConfigurations();
        if (response.status && response.data) {
          setConfigurations(response.data);
        }
      } catch (err) {
        console.error('Failed to load configurations:', err);
      }
    };
    loadConfigurations();
  }, []);

  // Load logs
  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await emailLogsService.getEmailLogs({
        ...filters,
        page: currentPage,
        limit
      });

      if (response.status && response.data) {
        setLogs(response.data.logs || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotal(response.data.pagination?.total || 0);
      }
    } catch (err) {
      setError(err.message || 'Failed to load email logs');
    } finally {
      setLoading(false);
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const response = await emailLogsService.getEmailLogStats({
        startDate: filters.startDate,
        endDate: filters.endDate
      });
      if (response.status && response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [currentPage, filters, limit]);

  // Reset to page 1 when limit changes
  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleViewDetails = async (logId) => {
    try {
      const response = await emailLogsService.getEmailLogById(logId);
      if (response.status && response.data) {
        setSelectedLog(response.data);
        setShowDetailsModal(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to load email log details');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      sent: 'success',
      failed: 'danger',
      pending: 'warning',
      partial_failed: 'warning'
    };
    const labels = {
      sent: 'Sent',
      failed: 'Failed',
      pending: 'Pending',
      partial_failed: 'Partial Failed'
    };
    return <Badge bg={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="email-logs-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Email Logs</h4>
        {stats && (
          <div className="d-flex gap-3">
            <small className="text-muted">
              <strong>Total:</strong> {stats.total} | 
              <strong> Sent:</strong> <span className="text-success">{stats.sent}</span> | 
              <strong> Failed:</strong> <span className="text-danger">{stats.failed}</span> | 
              <strong> Partial Failed:</strong> <span className="text-warning">{stats.partialFailed || 0}</span> | 
              <strong> Success Rate:</strong> {stats.successRate}%
            </small>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="email-logs-filters mb-3 p-3 bg-light rounded">
        <div className="row g-3">
          <div className="col-md-3">
            <Form.Label>Sender</Form.Label>
            <Form.Control
              type="text"
              placeholder="Filter by sender"
              value={filters.sender}
              onChange={(e) => handleFilterChange('sender', e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <Form.Label>Receiver</Form.Label>
            <Form.Control
              type="text"
              placeholder="Filter by receiver"
              value={filters.receiver}
              onChange={(e) => handleFilterChange('receiver', e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="partial_failed">Partial Failed</option>
              <option value="pending">Pending</option>
            </Form.Select>
          </div>
          <div className="col-md-2">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>
        </div>
        {configurations.length > 0 && (
          <div className="row g-3 mt-2">
            <div className="col-md-4">
              <Form.Label>Configuration</Form.Label>
              <Form.Select
                value={filters.configurationId}
                onChange={(e) => handleFilterChange('configurationId', e.target.value)}
              >
                <option value="">All Configurations</option>
                {configurations.map(config => (
                  <option key={config.id} value={config.id}>
                    {config.name}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
        )}
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted">No email logs found.</p>
        </div>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Sender</th>
                <th>Receiver</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Recipients</th>
                <th>Sent At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                // Calculate recipient status summary
                const recipients = log.recipients || [];
                const recipientSummary = recipients.length > 0 ? {
                  total: recipients.length,
                  sent: recipients.filter(r => r.status === 'sent').length,
                  failed: recipients.filter(r => r.status === 'failed').length,
                  pending: recipients.filter(r => r.status === 'pending').length
                } : null;

                return (
                  <tr key={log.id}>
                    <td>{log.sender}</td>
                    <td>{truncateText(log.receiver, 30)}</td>
                    <td>{truncateText(log.subject, 40)}</td>
                    <td>{getStatusBadge(log.status)}</td>
                    <td>
                      {recipientSummary ? (
                        <div className="small">
                          <div>
                            <Badge bg="success" className="me-1">
                              {recipientSummary.sent} sent
                            </Badge>
                            {recipientSummary.failed > 0 && (
                              <Badge bg="danger" className="me-1">
                                {recipientSummary.failed} failed
                              </Badge>
                            )}
                            {recipientSummary.pending > 0 && (
                              <Badge bg="warning" className="me-1">
                                {recipientSummary.pending} pending
                              </Badge>
                            )}
                          </div>
                          <small className="text-muted">
                            {recipientSummary.total} total
                          </small>
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{formatDate(log.sentAt || log.createdAt)}</td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleViewDetails(log.id)}
                      >
                        <Eye size={16} className="me-1" />
                        View
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {/* Pagination */}
          {(totalPages > 1 || true) && (
            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div>
                  <small className="text-muted">
                    Showing page {currentPage} of {totalPages} (Total: {total} logs)
                  </small>
                </div>
                {/* Items per page selector */}
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                    Show:
                  </span>
                  <Form.Select
                    value={limit}
                    onChange={(e) => handleLimitChange(parseInt(e.target.value, 10))}
                    size="sm"
                    style={{ width: 'auto', minWidth: '80px' }}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                  <span className="text-muted" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                    per page
                  </span>
                </div>
              </div>
              
              {/* Pagination controls */}
              {totalPages > 1 && (
                <Pagination className="mb-0">
                <Pagination.First
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                />
                {[...Array(totalPages)].map((_, idx) => {
                  const page = idx + 1;
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 2 && page <= currentPage + 2)
                  ) {
                    return (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  } else if (
                    page === currentPage - 3 ||
                    page === currentPage + 3
                  ) {
                    return <Pagination.Ellipsis key={page} />;
                  }
                  return null;
                })}
                <Pagination.Next
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                />
                </Pagination>
              )}
            </div>
          )}
        </>
      )}

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Email Log Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLog && (
            <div>
              <div className="mb-3">
                <strong>Sender:</strong> {selectedLog.sender}
              </div>
              <div className="mb-3">
                <strong>Receiver:</strong> {selectedLog.receiver}
              </div>
              <div className="mb-3">
                <strong>Subject:</strong> {selectedLog.subject}
              </div>
              <div className="mb-3">
                <strong>Overall Status:</strong> {getStatusBadge(selectedLog.status)}
              </div>
              {selectedLog.recipients && selectedLog.recipients.length > 0 && (
                <div className="mb-3">
                  <strong>Recipients Status:</strong>
                  <div className="mt-2">
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Email</th>
                          <th>Status</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedLog.recipients.map((recipient) => (
                          <tr key={recipient.id}>
                            <td>
                              <Badge bg="info" text="dark">
                                {recipient.recipientType.toUpperCase()}
                              </Badge>
                            </td>
                            <td>{recipient.email}</td>
                            <td>{getStatusBadge(recipient.status)}</td>
                            <td>
                              {recipient.errorMessage ? (
                                <small className="text-danger">{recipient.errorMessage}</small>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
              <div className="mb-3">
                <strong>Sent At:</strong> {formatDate(selectedLog.sentAt || selectedLog.createdAt)}
              </div>
              {selectedLog.configuration && (
                <div className="mb-3">
                  <strong>Configuration:</strong> {selectedLog.configuration.name} ({selectedLog.configuration.host})
                </div>
              )}
              {selectedLog.errorMessage && (
                <div className="mb-3">
                  <strong>Error:</strong>
                  <Alert variant="danger" className="mt-2">
                    {selectedLog.errorMessage}
                  </Alert>
                </div>
              )}
              {selectedLog.body && (
                <div className="mb-3">
                  <strong>Body:</strong>
                  <div className="border p-3 mt-2" style={{ maxHeight: '400px', overflow: 'auto' }}>
                    {selectedLog.bodyType === 'html' ? (
                      <div dangerouslySetInnerHTML={{ __html: selectedLog.body }} />
                    ) : (
                      <pre style={{ whiteSpace: 'pre-wrap' }}>{selectedLog.body}</pre>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmailLogsTable;

